import { AMBIENT, mountAmbientBackground } from '../../engine/ambient-background';
import { icon, type IconName } from '../../engine/icons';
import type { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { fetchQuests, claimQuestReward, fetchAchievements, fetchDailyStatus } from './quests-api';
import { claimBurst, glitchOnce } from '../../engine/glitch';
import type { UserQuestView, AchievementView, DailyStatus } from './quests-api';
import { formatClock } from '../../engine/boosts';
import { t, type Translations } from '../../engine/i18n/i18n';

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * Player-facing badge copy per achievement key. GET /achievements is only
 * asked for `key` + `unlocked` (see AchievementView in quests-api.ts), so
 * display text lives here — same pattern as colony-screen.ts's
 * BUILDING_META/UNIT_META dictionaries keyed by a server-supplied id. An
 * achievement the server returns but this map doesn't recognise (added to
 * the catalog later without a client update) still renders, via the
 * fallbacks in achievementTitle/achievementDescription below.
 */
const ACHIEVEMENT_META: Record<string, { icon: IconName }> = {
  achv_bastion_wall_5: { icon: 'wall' },
  achv_tech_level_5: { icon: 'research' },
  achv_standing_army_100: { icon: 'battle' },
};

/** 'achv_bastion_wall_5' -> 'Bastion Wall 5' — last-resort title for an unrecognised key with no server-supplied label. */
function prettifyKey(key: string): string {
  return key
    .replace(/^achv_/, '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function achievementTitle(a: AchievementView): string {
  return ACHIEVEMENT_META[a.key] ? t(`quests.achv.${a.key}.title` as keyof Translations) : (a.label ?? prettifyKey(a.key));
}
function achievementDescription(a: AchievementView): string {
  return ACHIEVEMENT_META[a.key] ? t(`quests.achv.${a.key}.description` as keyof Translations) : (a.description ?? '');
}
function achievementIcon(a: AchievementView): string {
  return icon(ACHIEVEMENT_META[a.key]?.icon ?? 'medal');
}

/** After the reset: how often, and how many times, to look for the freshly assigned dailies. */
const ROLLOVER_POLLS = 10;
const ROLLOVER_POLL_MS = 3000;

export function renderQuestsScreen(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="relative w-full h-full">
      <div data-ambient class="absolute inset-0 overflow-hidden pointer-events-none"></div>
      <div id="quests-screen" class="relative w-full h-full overflow-y-auto">
        <div class="text-center text-text-dim py-10">${t('quests.loading')}</div>
      </div>
    </div>
  `;
  mountAmbientBackground(root.querySelector('[data-ambient]') as HTMLElement, AMBIENT.quests);
  const container = root.querySelector('#quests-screen') as HTMLDivElement;

  let disposed = false;
  let quests: UserQuestView[] | null = null;
  let questsFailed = false;
  let achievements: AchievementView[] | null = null;
  let achievementsFailed = false;
  /** Dailies claimed in the current window — kept on show, styled as done. Empty when the status call failed. */
  let claimedDailies: UserQuestView[] = [];
  /** Server's next-dailies instant, and how far the server clock runs ahead of ours. Null hides the timer block. */
  let nextDailiesAt: number | null = null;
  let serverSkewMs = 0;
  /** In-flight claim requests, so a double-tap on the same quest can't fire two POSTs. */
  const claimingIds = new Set<string>();

  // ---------------------------------------------------------------- daily quests

  function questRowHtml(uq: UserQuestView): string {
    const target = uq.quest.targetCount;
    const pct = target > 0 ? Math.min(100, (uq.progress / target) * 100) : 0;
    const complete = uq.status === 'COMPLETED';
    const claiming = claimingIds.has(uq.id);
    const claimed = uq.status === 'CLAIMED';
    const rewardParts: string[] = [];
    if (uq.quest.rewardSoft > 0) rewardParts.push(`${icon('coins')} ${uq.quest.rewardSoft}`);
    if (uq.quest.rewardPremium > 0) rewardParts.push(`${icon('gem')} ${uq.quest.rewardPremium}`);
    if (uq.quest.rewardXp > 0) rewardParts.push(`${icon('star')} ${uq.quest.rewardXp} XP`);

    return `
      <div class="glass-panel p-3 flex flex-col gap-2 quest-row${complete ? ' quest-row--ready' : ''}${claimed ? ' quest-row--claimed' : ''}" data-quest-row="${uq.id}" data-quest-state="${claimed ? 'claimed' : complete ? 'ready' : 'active'}">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="text-sm text-text quest-name">${escapeHtml(uq.quest.name)}</div>
            <div class="text-[10px] text-text-dim leading-relaxed">${escapeHtml(uq.quest.description)}</div>
          </div>
          <div class="text-[10px] text-text-dim tabular-nums shrink-0" data-quest-fraction>${claimed ? `${icon('check')}` : `${uq.progress} / ${target}`}</div>
        </div>
        <div class="h-1.5 w-full bg-surface-2 rounded overflow-hidden">
          <div class="h-full bg-gold transition-all duration-[200ms]" data-quest-progress-bar style="width:${claimed ? 100 : pct}%"></div>
        </div>
        <div class="flex items-center justify-between gap-2 flex-wrap">
          <div class="text-[10px] text-text-dim tabular-nums">${rewardParts.join(' &middot; ') || '&nbsp;'}</div>
          ${
            claimed
              ? `<div class="quest-claimed-badge" data-quest-claimed>${icon('check')}<span>${t('quests.claimed')}</span></div>`
              : `<button type="button" data-action="claim-quest" data-user-quest-id="${uq.id}" ${complete && !claiming ? '' : 'disabled'}
            class="font-button min-h-11 px-4 bg-gold text-bg text-xs rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation shrink-0">
            ${claiming ? t('quests.claiming') : t('quests.claim')}
          </button>`
          }
        </div>
      </div>
    `;
  }

  function dailyQuestsSectionHtml(): string {
    if (questsFailed) {
      return `<p class="text-xs text-text-dim">${t('quests.error.quests')}</p>`;
    }
    if (!quests) {
      return `<p class="text-xs text-text-dim">${t('common.loading')}</p>`;
    }
    // GET /quests also returns WEEKLY/MILESTONE/SEASONAL rows once those
    // exist in the catalog — this screen only has a Daily Quests section
    // today, so non-DAILY rows are deliberately filtered out here rather
    // than silently merged in (a completed WEEKLY would otherwise be
    // invisible-and-unclaimable). Moot while the catalog is DAILY-only.
    const daily = quests.filter((uq) => uq.quest.questType === 'DAILY');
    // A row being claimed right now is still COMPLETED here (the refetch that flips it lands after the burst).
    // Ready-to-claim first, then in progress, then the done ones, dimmed at the bottom.
    const rank = (uq: UserQuestView) => (uq.status === 'COMPLETED' ? 0 : 1);
    const open = [...daily].sort((a, b) => rank(a) - rank(b));
    const done = claimedDailies.filter((uq) => !daily.some((d) => d.id === uq.id));
    if (open.length + done.length === 0) {
      return `<p class="text-xs text-text-dim">${t('quests.empty.daily')}</p>`;
    }
    return [...open, ...done].map(questRowHtml).join('');
  }

  /** "New dailies in 07:42:10" — absent until the server has told us when the next batch lands. */
  function dailiesTimerHtml(): string {
    if (nextDailiesAt === null) return '';
    const left = nextDailiesAt - (Date.now() - serverSkewMs);
    return `
      <div class="quest-timer" role="timer" data-dailies-timer>
        <span class="quest-timer-ico" aria-hidden="true">${icon('clock')}</span>
        <div class="min-w-0">
          <div class="quest-timer-label">${t('quests.nextDailies')}</div>
          <div class="quest-timer-clock tabular-nums" data-dailies-reset>${formatClock(left)}</div>
          <div class="quest-timer-note">${t('quests.nextDailies.note', { time: new Date(nextDailiesAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}</div>
        </div>
      </div>`;
  }

  // ---------------------------------------------------------------- achievements

  function achievementCardHtml(a: AchievementView): string {
    const title = escapeHtml(achievementTitle(a));
    const description = escapeHtml(achievementDescription(a));
    const icon = achievementIcon(a);
    const key = escapeHtml(a.key);
    if (a.unlocked) {
      return `
        <div class="chip-on rounded-lg p-3 flex items-center gap-3" data-achievement="${key}" data-unlocked="true">
          <span class="text-2xl shrink-0" aria-hidden="true">${icon}</span>
          <div class="min-w-0">
            <div class="text-sm text-text">${title}</div>
            <div class="text-[10px] text-text-dim">${description}</div>
          </div>
        </div>
      `;
    }
    // Locked cards deliberately carry no title=/tooltip — this app's
    // established convention for disabled/locked states (see the
    // tech-gated construct/train controls in colony-screen.ts, none of
    // which explain themselves via a tooltip either) is to just show the
    // muted state, not over-decorate it.
    return `
      <div class="chip-off rounded-lg p-3 flex items-center gap-3" data-achievement="${key}" data-unlocked="false">
        <span class="text-2xl shrink-0 grayscale opacity-60" aria-hidden="true">${icon}</span>
        <div class="min-w-0">
          <div class="text-sm text-text-dim">${title}</div>
          <div class="text-[10px] text-text-dim">${description}</div>
        </div>
      </div>
    `;
  }

  function achievementsSectionHtml(): string {
    if (achievementsFailed) {
      return `<p class="text-xs text-text-dim">${t('quests.error.achievements')}</p>`;
    }
    if (!achievements) {
      return `<p class="text-xs text-text-dim">${t('common.loading')}</p>`;
    }
    if (achievements.length === 0) {
      return `<p class="text-xs text-text-dim">${t('quests.empty.achievements')}</p>`;
    }
    return achievements.map(achievementCardHtml).join('');
  }

  // ------------------------------------------------------------------ render

  function render(): void {
    container.innerHTML = `
      <div class="max-w-2xl mx-auto p-3 sm:p-4 flex flex-col gap-4">
        <h1 class="font-heading text-gold text-base">${t('quests.heading')}</h1>
        <section>
          <h2 class="font-heading text-xs text-gold mb-2 tracking-wide uppercase">${t('quests.daily')}</h2>
          ${dailiesTimerHtml()}
          <div class="flex flex-col gap-2" data-daily-quests-section>${dailyQuestsSectionHtml()}</div>
        </section>
        <section>
          <h2 class="font-heading text-xs text-gold mb-2 tracking-wide uppercase">${t('quests.achievements')}</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2" data-achievements-section>${achievementsSectionHtml()}</div>
        </section>
      </div>
    `;
  }

  // ------------------------------------------------------------------- data

  /**
   * Quests and achievements are independent sources — a failure in one must
   * not blank the other section (same allSettled resilience as
   * profile-screen.ts's load()).
   */
  async function load(): Promise<void> {
    const [questsResult, achievementsResult, statusResult] = await Promise.allSettled([fetchQuests(), fetchAchievements(), fetchDailyStatus()]);
    if (disposed) return;

    if (questsResult.status === 'fulfilled') {
      quests = questsResult.value;
    } else {
      questsFailed = true;
      const message = questsResult.reason instanceof Error ? questsResult.reason.message : t('quests.toast.loadFailed');
      showToast(message, 'error');
    }

    if (achievementsResult.status === 'fulfilled') {
      achievements = achievementsResult.value;
    } else {
      achievementsFailed = true;
      const message =
        achievementsResult.reason instanceof Error ? achievementsResult.reason.message : t('quests.toast.achievementsFailed');
      showToast(message, 'error');
    }

    // The claimed list + countdown are garnish: without them the screen is just the plain list, so no toast.
    if (statusResult.status === 'fulfilled') applyDailyStatus(statusResult.value);

    render();
  }

  function applyDailyStatus(status: DailyStatus): void {
    claimedDailies = Array.isArray(status.claimed) ? status.claimed : [];
    const next = new Date(status.nextDailiesAt).getTime();
    nextDailiesAt = Number.isFinite(next) ? next : null;
    const serverNow = new Date(status.now).getTime();
    serverSkewMs = Number.isFinite(serverNow) ? Date.now() - serverNow : 0;
  }

  /** Ids of the dailies that were on screen when the reset hit; never shown again, even if the server still returns them. */
  const staleDailyIds = new Set<string>();

  async function refetchQuests(): Promise<void> {
    try {
      const [result, status] = await Promise.all([fetchQuests(), fetchDailyStatus().catch(() => null)]);
      if (disposed) return;
      quests = result.filter((uq) => !staleDailyIds.has(uq.id));
      questsFailed = false;
      if (status) applyDailyStatus(status);
      claimedDailies = claimedDailies.filter((uq) => !staleDailyIds.has(uq.id));
      render();
    } catch {
      // Keep the last-known list; the next manual retry (revisit/reload) picks it up.
    }
  }

  // ---------------------------------------------------------------- actions

  async function handleClaim(userQuestId: string): Promise<void> {
    if (claimingIds.has(userQuestId)) return;
    claimingIds.add(userQuestId);
    render();
    try {
      await claimQuestReward(userQuestId);
      if (disposed) return;
      showToast(t('quests.toast.claimed'), 'success');
      // Celebrate before the row goes: it stays on screen (the button is held by claimingIds)
      // while it tears and flares, then the list is rebuilt without it.
      const row = root.querySelector(`[data-quest-row="${userQuestId}"]`) as HTMLElement | null;
      if (row) {
        const button = row.querySelector('[data-action="claim-quest"]');
        if (button) glitchOnce(button, 'hard');
        await claimBurst(row);
        if (disposed) return;
      }
      claimingIds.delete(userQuestId);
      // Clear the "Claiming…" state up front, independent of the refetch below. A claimed quest is no longer in
      // GET /quests, but the refetch also reads GET /quests/daily-status, which hands it back as a claimed daily —
      // so it stays on the list, dimmed and ticked, rather than vanishing. If the refetch fails, this render()
      // still recovers the button from being stuck disabled (it shows its last-known COMPLETED state instead).
      render();
      await refetchQuests();
    } catch (err) {
      if (disposed) return;
      claimingIds.delete(userQuestId);
      showToast(err instanceof Error ? err.message : t('quests.toast.claimFailed'), 'error');
      render();
    }
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    if (action === 'claim-quest') {
      const id = actionEl.dataset.userQuestId;
      if (id) void handleClaim(id);
    }
  }
  root.addEventListener('click', onClick);

  void load();

  /**
   * The daily reset: the previous set — open AND claimed — and the countdown leave the screen the moment the clock
   * hits zero. The new set is then polled for, because the assignment job runs a little after the nominal instant
   * (it walks every player), and until it lands the old rows must not creep back from a stale response.
   */
  let rolling = false;
  async function rollOver(): Promise<void> {
    for (const uq of quests ?? []) if (uq.quest.questType === 'DAILY') staleDailyIds.add(uq.id);
    for (const uq of claimedDailies) staleDailyIds.add(uq.id);
    quests = (quests ?? []).filter((uq) => !staleDailyIds.has(uq.id));
    claimedDailies = [];
    nextDailiesAt = null;
    render();
    for (let attempt = 0; attempt < ROLLOVER_POLLS && !disposed; attempt++) {
      await refetchQuests();
      if (disposed || (quests ?? []).some((uq) => uq.quest.questType === 'DAILY')) break;
      await new Promise((r) => window.setTimeout(r, ROLLOVER_POLL_MS));
    }
  }

  // One-second tick for the "new dailies in…" clock. Touches only its own text node; at zero it rolls the dailies over.
  const timerTick = window.setInterval(() => {
    if (disposed || nextDailiesAt === null) return;
    const left = nextDailiesAt - (Date.now() - serverSkewMs);
    const el = container.querySelector('[data-dailies-reset]');
    if (el) el.textContent = formatClock(left);
    if (left <= 0 && !rolling) {
      rolling = true;
      void rollOver().finally(() => {
        rolling = false;
      });
    }
  }, 1000);

  return () => {
    disposed = true;
    window.clearInterval(timerTick);
    root.removeEventListener('click', onClick);
  };
}

export const questsTab: GameTab = {
  id: 'quests',
  label: t('nav.quests'),
  icon: 'quests',
  mount: renderQuestsScreen,
};
